import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronRight, GripVertical, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { invalidateNavbarCache } from "../hooks/useNavbarConfig";
import BulkPauseEditor from "./BulkPauseEditor";
import FlexfuelOfferSettings from "./FlexfuelOfferSettings";
import VacationBannerSettings from "./VacationBannerSettings";
import { CALC_TEXT_DEFAULTS, CALC_TEXT_FIELDS } from "../components/sections/flexfuelCalculatorText";
import { HomeSectionsEditor } from "./HomeSectionsEditor";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  {
    id: "pages",
    label: "Pages publiques",
    icon: "📄",
    keys: [
      { key: "home_landing", label: "Site principal" },
      { key: "flexfuel_page", label: "Page Flexfuel" },
      { key: "reprog_page", label: "Page Optimisation" },
      { key: "achat_vente_page", label: "Page Dépôt-Vente" },
      { key: "rep_entretien_page", label: "Page Mécanique" },
      { key: "shop_landing", label: "Page Boutique" },
      { key: "fs_landing", label: "Service Fichiers" },
    ],
  },
  {
    id: "global",
    label: "Composants globaux",
    icon: "🧩",
    keys: [
      { key: "contact_info", label: "📍 Coordonnées (site + emails + PDFs)" },
      { key: "navbar", label: "Menu haut (navbar)" },
      { key: "banner", label: "Bannière du site" },
    ],
  },
  {
    id: "dynamic",
    label: "Contenu dynamique",
    icon: "📦",
    keys: [
      { key: "discharge", label: "Décharge de responsabilité" },
      { key: "email_templates", label: "Emails automatiques" },
    ],
  },
  {
    id: "seo",
    label: "SEO & Marketing",
    icon: "🎯",
    keys: [
      { key: "site_meta", label: "Méta du site (SEO)" },
      { key: "review_nudge", label: "Nudge avis Google" },
    ],
  },
];

// CategoryPicker — collapsible group of page keys. On desktop it stays open,
// on mobile only the group containing the currently-edited key is expanded.
const CategoryPicker = ({ cat, currentKey, setCurrentKey, defaultOpenMobile }) => {
  const [openMobile, setOpenMobile] = useState(defaultOpenMobile);
  // Whenever the current key changes to a key inside this category, auto-open.
  useEffect(() => {
    if (cat.keys.some((k) => k.key === currentKey)) setOpenMobile(true);
  }, [currentKey, cat.keys]);

  return (
    <div data-testid={`content-category-${cat.id}`}>
      {/* Header — clickable on mobile only (accordion), static on desktop */}
      <button
        type="button"
        onClick={() => setOpenMobile((v) => !v)}
        className="w-full sm:cursor-default flex items-center gap-2 mb-2 text-left"
      >
        <span className="text-lg leading-none">{cat.icon}</span>
        <h3 className="text-white/50 text-xs font-bold uppercase tracking-wider">{cat.label}</h3>
        <div className="flex-1 h-px bg-white/[0.06]" />
        <ChevronDown
          size={16}
          className={`sm:hidden text-white/40 transition-transform ${openMobile ? "" : "-rotate-90"}`}
        />
      </button>
      <div className={`${openMobile ? "grid" : "hidden"} sm:flex sm:flex-wrap grid-cols-2 gap-2`}>
        {cat.keys.map((k) => (
          <button
            key={k.key}
            onClick={() => setCurrentKey(k.key)}
            data-testid={`content-key-${k.key}`}
            className={`px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium rounded-md transition-colors text-left sm:text-center ${
              currentKey === k.key
                ? "bg-[#FF3B30] text-white shadow-lg shadow-[#FF3B30]/20"
                : "bg-[#121212] text-white/60 hover:text-white hover:bg-[#1C1C1E] border border-white/[0.06]"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, multiline = false, placeholder = "", testId }) => (
  <div>
    <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">
      {label}
    </label>
    {multiline ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        rows={3}
        className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2.5 md:py-2 text-white text-base md:text-sm focus:outline-none focus:border-white/20"
      />
    ) : (
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2.5 md:py-2 text-white text-base md:text-sm focus:outline-none focus:border-white/20"
      />
    )}
  </div>
);

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#121212] border border-white/[0.06] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02]"
      >
        <span className="text-white font-semibold text-sm uppercase tracking-wider">
          {title}
        </span>
        {open ? <ChevronDown size={16} className="text-white/40" /> : <ChevronRight size={16} className="text-white/40" />}
      </button>
      {open && <div className="p-5 pt-0 space-y-4">{children}</div>}
    </div>
  );
};

// PageSEOEditor — reusable block for editing per-page SEO title/description + H1 (split into white + red highlight).
// Used at the top of every page editor to give admins a single place to control what appears in the browser tab,
// Google search results, and as the main visible heading of the page.
const PageSEOEditor = ({ data, setData, pageLabel, pageRoute }) => {
  const seo = data.seo || {};
  const setSeo = (patch) => setData({ ...data, seo: { ...seo, ...patch } });
  const titleLen = (seo.title || "").length;
  const descLen = (seo.description || "").length;
  const titleColor = titleLen === 0 ? "text-white/40" : titleLen <= 60 ? "text-green-400" : "text-yellow-400";
  const descColor = descLen === 0 ? "text-white/40" : descLen <= 155 ? "text-green-400" : "text-yellow-400";

  return (
    <Section title="📛 Nom de la page & SEO (onglet navigateur, Google, H1)">
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70 space-y-1">
        <div>💡 Cette section contrôle <strong>4 éléments visibles</strong> de la page {pageLabel}{pageRoute ? <> (<code className="text-white/90">{pageRoute}</code>)</> : null} :</div>
        <ul className="ml-4 list-disc space-y-0.5 text-white/60">
          <li><strong>Titre SEO</strong> — s&apos;affiche dans l&apos;onglet du navigateur et comme titre bleu sur Google</li>
          <li><strong>Description SEO</strong> — le texte gris sous le titre dans les résultats Google</li>
          <li><strong>H1</strong> — le grand titre visible tout en haut de la page (partie blanche + partie rouge)</li>
        </ul>
      </div>

      <div>
        <Field
          testId="content-seo-title"
          label={`Titre SEO (${titleLen} caractères — sweet spot Google 50-60)`}
          value={seo.title}
          onChange={(v) => setSeo({ title: v })}
          placeholder="Titre affiché dans l'onglet du navigateur et sur Google"
        />
        <p className={`text-[11px] mt-1 ${titleColor}`}>
          {titleLen === 0
            ? "Utilise le titre par défaut du site"
            : titleLen <= 60
            ? `✓ Longueur idéale (${titleLen}/60)`
            : `⚠ Un peu long (${titleLen}/60) — Google risque de tronquer`}
        </p>
      </div>

      <div>
        <Field
          testId="content-seo-description"
          label={`Description SEO (${descLen} caractères — max 155)`}
          value={seo.description}
          onChange={(v) => setSeo({ description: v })}
          multiline
          placeholder="Description affichée sous le titre bleu dans les résultats Google"
        />
        <p className={`text-[11px] mt-1 ${descColor}`}>
          {descLen === 0
            ? "Utilise la description par défaut du site"
            : descLen <= 155
            ? `✓ Longueur idéale (${descLen}/155)`
            : `⚠ Un peu long (${descLen}/155) — Google risque de tronquer`}
        </p>
      </div>

      <div className="pt-2 border-t border-white/[0.04]">
        <p className="text-white/50 text-xs uppercase tracking-wider mb-2 font-semibold">Titre H1 (grand titre visible en haut de la page)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            testId="content-seo-h1-prefix"
            label="Partie principale (texte blanc)"
            value={seo.h1_prefix}
            onChange={(v) => setSeo({ h1_prefix: v })}
            placeholder="Ex : Reprogrammation Moteur"
          />
          <Field
            testId="content-seo-h1-highlight"
            label="Partie mise en avant (texte rouge)"
            value={seo.h1_highlight}
            onChange={(v) => setSeo({ h1_highlight: v })}
            placeholder="Ex : à Clermont-Ferrand"
          />
        </div>
        {(seo.h1_prefix || seo.h1_highlight) && (
          <div className="mt-3 bg-black/40 border border-white/[0.04] rounded-md p-4">
            <p className="text-white/40 text-[10px] uppercase tracking-widest mb-2">Aperçu du H1</p>
            <div className="text-2xl md:text-3xl font-black tracking-tight leading-tight">
              <span className="text-white">{seo.h1_prefix || ""}</span>
              {seo.h1_prefix && seo.h1_highlight && <br />}
              {seo.h1_highlight && <span className="text-[#FF3B30]">{seo.h1_highlight}</span>}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

const ShopLandingEditor = ({ data, setData }) => (
  <>
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
      💡 Cette page ne concerne que le <strong>nommage SEO</strong> de la boutique <code className="text-white/90">/shop</code>. Les produits eux-mêmes se gèrent depuis l&apos;onglet <strong>Boutique</strong>.
    </div>
    <PageSEOEditor data={data} setData={setData} pageLabel="Boutique" pageRoute="/shop" />
  </>
);

const SortableServiceCardRow = ({ s, draft, onDraftChange, dirty, savingId, onSave }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`content-service-card-${s.id}`}
      className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            data-testid={`content-service-drag-${s.id}`}
            className="text-white/30 hover:text-white/60 cursor-grab active:cursor-grabbing"
            aria-label="Réordonner"
          >
            <GripVertical size={16} />
          </button>
          <span className="text-white/40 text-xs font-mono">{s.id}</span>
        </div>
        {dirty && (
          <button
            onClick={() => onSave(s)}
            disabled={savingId === s.id}
            data-testid={`content-service-save-${s.id}`}
            className="bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded"
          >
            {savingId === s.id ? "Enregistrement…" : "Enregistrer"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Titre" value={draft.name} onChange={(v) => onDraftChange(s, { name: v })} />
        </div>
        <div>
          <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Durée (min)</label>
          <input
            type="number"
            value={draft.duration}
            onChange={(e) => onDraftChange(s, { duration: e.target.value })}
            className="w-full bg-[#121212] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
          />
        </div>
      </div>
      <Field
        label="Tarif affiché (vide = « Sur devis » par défaut)"
        value={draft.starting_price_label}
        onChange={(v) => onDraftChange(s, { starting_price_label: v })}
        placeholder="ex : À partir de 300 € TTC"
      />
      <Field label="Description" value={draft.description} onChange={(v) => onDraftChange(s, { description: v })} multiline />
      <div>
        <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">
          Indisponible jusqu'au <span className="normal-case text-white/30">(vacances / pause — vide = disponible)</span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={draft.unavailable_until || ""}
            onChange={(e) => onDraftChange(s, { unavailable_until: e.target.value })}
            data-testid={`content-service-unavailable-${s.id}`}
            className="bg-[#121212] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
            style={{ minHeight: 44 }}
          />
          {draft.unavailable_until && (
            <>
              <span className="text-white/60 text-xs">
                Reprise le {new Date(draft.unavailable_until + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              <button
                type="button"
                onClick={() => onDraftChange(s, { unavailable_until: "" })}
                data-testid={`content-service-unavailable-clear-${s.id}`}
                className="text-white/50 hover:text-white text-xs underline"
              >
                Effacer
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ServicesCardsEditor = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [bannerRefresh, setBannerRefresh] = useState(0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/services`)
      .then(r => setServices(r.data))
      .catch(() => toast.error("Erreur au chargement des services"))
      .finally(() => setLoading(false));
  }, []);

  const getDraft = (s) => drafts[s.id] || {
    name: s.name,
    description: s.description,
    duration: s.duration,
    starting_price_label: s.starting_price_label || "",
    unavailable_until: s.unavailable_until || "",
  };

  const handleDraftChange = (s, patch) => {
    setDrafts((prev) => ({ ...prev, [s.id]: { ...(prev[s.id] || getDraft(s)), ...patch } }));
  };

  const isDirty = (s) => {
    const d = drafts[s.id];
    if (!d) return false;
    return d.name !== s.name
      || d.description !== s.description
      || Number(d.duration) !== Number(s.duration)
      || (d.starting_price_label || "") !== (s.starting_price_label || "")
      || (d.unavailable_until || "") !== (s.unavailable_until || "");
  };

  const save = async (s) => {
    setSavingId(s.id);
    try {
      const d = getDraft(s);
      const res = await axios.put(`${API}/api/admin/services/${s.id}`, {
        name: d.name,
        description: d.description,
        duration: Number(d.duration),
        starting_price_label: d.starting_price_label,
        unavailable_until: d.unavailable_until || null,
      });
      setServices((prev) => prev.map(x => x.id === s.id ? res.data : x));
      setBannerRefresh((n) => n + 1);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[s.id];
        return next;
      });
      toast.success(`« ${res.data.name} » enregistré`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur à l'enregistrement");
    } finally {
      setSavingId(null);
    }
  };

  const handleBulkUpdated = (updated) => {
    setBannerRefresh((n) => n + 1);
    const byId = Object.fromEntries(updated.map((u) => [u.id, u]));
    setServices((prev) => prev.map((x) => byId[x.id] || x));
    setDrafts((prev) => {
      const next = { ...prev };
      updated.forEach((u) => {
        if (next[u.id]) next[u.id] = { ...next[u.id], unavailable_until: u.unavailable_until || "" };
      });
      return next;
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = services.findIndex(s => s.id === active.id);
    const newIdx = services.findIndex(s => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(services, oldIdx, newIdx);
    setServices(reordered); // optimistic UI
    setReordering(true);
    try {
      await axios.post(`${API}/api/admin/services/reorder`, {
        order: reordered.map(s => s.id),
      });
      toast.success("Ordre enregistré");
    } catch (err) {
      toast.error("Erreur au réordonnement");
    } finally {
      setReordering(false);
    }
  };

  return (
    <Section title="Cartes de services (page d'accueil)">
      <p className="text-white/40 text-xs -mt-1">
        Modifie titre, description, tarif et durée. Glisse-dépose via <GripVertical size={12} className="inline -mt-0.5" /> pour réordonner.
      </p>
      {reordering && <p className="text-[#FF3B30] text-xs">Enregistrement de l&apos;ordre…</p>}
      {loading && <p className="text-white/40 text-sm">Chargement…</p>}
      {!loading && services.length > 0 && (
        <>
          <BulkPauseEditor services={services} onUpdated={handleBulkUpdated} />
          <VacationBannerSettings refreshKey={bannerRefresh} />
        </>
      )}
      {!loading && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={services.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {services.map((s) => (
                <SortableServiceCardRow
                  key={s.id}
                  s={s}
                  draft={getDraft(s)}
                  onDraftChange={handleDraftChange}
                  dirty={isDirty(s)}
                  savingId={savingId}
                  onSave={save}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </Section>
  );
};

const SiteMetaEditor = ({ data, setData }) => (
  <>
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
      💡 Ces champs contrôlent le titre dans l&apos;onglet du navigateur, l&apos;aperçu Google, et l&apos;aperçu lors d&apos;un partage sur Facebook / WhatsApp / Twitter / LinkedIn. Les changements sont visibles immédiatement (rafraîchis la page publique pour voir).
    </div>

    <Section title="Onglet navigateur & SEO Google">
      <Field label="Titre de l'onglet (≤ 60 caractères pour Google)" value={data.title} onChange={(v) => setData({ ...data, title: v })} placeholder="Auvergne Tech Racing - Performance & Précision" />
      <Field label="Description SEO (≤ 155 caractères pour Google)" value={data.description} onChange={(v) => setData({ ...data, description: v })} multiline placeholder="Spécialiste Conversion Flexfuel E85 & Reprogrammation Moteur..." />
      <Field label="Mots-clés (séparés par virgules)" value={data.keywords} onChange={(v) => setData({ ...data, keywords: v })} placeholder="Flexfuel, E85, reprogrammation moteur, ECU..." />
    </Section>

    <Section title="Aperçu de partage social (Facebook, WhatsApp, LinkedIn)">
      <Field label="Titre Open Graph" value={data.og_title} onChange={(v) => setData({ ...data, og_title: v })} placeholder="Auvergne Tech Racing — Spécialiste Flexfuel..." />
      <Field label="Description Open Graph" value={data.og_description} onChange={(v) => setData({ ...data, og_description: v })} multiline placeholder="Conversion Flexfuel E85 & cartographie ECU..." />
      <Field label="URL de l'image d'aperçu (1200×630 idéal)" value={data.og_image} onChange={(v) => setData({ ...data, og_image: v })} placeholder="https://..." />
      {data.og_image && (
        <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3">
          <p className="text-white/40 text-[11px] uppercase tracking-wider mb-2">Aperçu</p>
          <img
            src={data.og_image}
            alt="OG preview"
            className="w-full max-w-md rounded-md border border-white/[0.06]"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
        </div>
      )}
    </Section>

    <Section title="Aperçu Twitter / X">
      <Field label="Titre Twitter" value={data.twitter_title} onChange={(v) => setData({ ...data, twitter_title: v })} placeholder="Auvergne Tech Racing — Flexfuel & Reprog Moteur" />
      <Field label="Description Twitter" value={data.twitter_description} onChange={(v) => setData({ ...data, twitter_description: v })} multiline />
      <p className="text-white/30 text-xs">L&apos;image utilisée pour Twitter est la même que pour Open Graph (champ « URL de l&apos;image d&apos;aperçu » plus haut).</p>
    </Section>
  </>
);

const ReviewNudgeEditor = ({ data, setData }) => (
  <>
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
      💡 Ce petit toast apparaît en bas à droite de la page d&apos;accueil pour inciter tes visiteurs contents à laisser un avis Google. Il s&apos;affiche <strong>une seule fois par visiteur</strong> pendant la période de cooldown configurée ci-dessous.
    </div>

    <Section title="Activation & timing">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.enabled !== false}
          onChange={(e) => setData({ ...data, enabled: e.target.checked })}
          data-testid="content-nudge-enabled"
          className="w-4 h-4 accent-[#FF3B30]"
        />
        <span className="text-white text-sm">Activer le nudge d&apos;avis Google</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Délai avant affichage (secondes)</label>
          <input
            type="number"
            min={0}
            value={data.delay_seconds ?? 30}
            onChange={(e) => setData({ ...data, delay_seconds: parseInt(e.target.value, 10) || 0 })}
            data-testid="content-nudge-delay"
            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
          />
          <p className="text-white/30 text-[11px] mt-1">Temps d&apos;attente avant que le nudge apparaisse (par défaut 30 s)</p>
        </div>
        <div>
          <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Cooldown après un dismiss (jours)</label>
          <input
            type="number"
            min={0}
            value={data.cooldown_days ?? 30}
            onChange={(e) => setData({ ...data, cooldown_days: parseInt(e.target.value, 10) || 0 })}
            data-testid="content-nudge-cooldown"
            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
          />
          <p className="text-white/30 text-[11px] mt-1">Durée pendant laquelle le nudge ne réapparaîtra pas après un rejet</p>
        </div>
      </div>
    </Section>

    <Section title="Textes affichés">
      <Field testId="content-nudge-title" label="Titre" value={data.title} onChange={(v) => setData({ ...data, title: v })} placeholder="Content de nos services ?" />
      <Field testId="content-nudge-subtitle" label="Sous-titre / message" value={data.subtitle} onChange={(v) => setData({ ...data, subtitle: v })} multiline placeholder="Un avis Google nous ferait vraiment plaisir..." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field testId="content-nudge-cta" label="Bouton principal (CTA)" value={data.cta_text} onChange={(v) => setData({ ...data, cta_text: v })} placeholder="Laisser un avis" />
        <Field testId="content-nudge-later" label="Bouton secondaire" value={data.later_text} onChange={(v) => setData({ ...data, later_text: v })} placeholder="Plus tard" />
      </div>
    </Section>
  </>
);

const ContactInfoEditor = ({ data, setData }) => {
  const set = (patch) => setData({ ...data, ...patch });
  return (
    <>
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        📍 <strong>Source unique de vérité</strong> — ces coordonnées alimentent automatiquement le site public, les fiches PDF, les emails de confirmation et les schémas SEO. Modifie ici, tout se met à jour partout.
      </div>

      <Section title="Identité de l'atelier">
        <Field testId="contact-business-name" label="Nom commercial" value={data.business_name} onChange={(v) => set({ business_name: v })} placeholder="Auvergne Tech Racing" />
      </Section>

      <Section title="Téléphone & Email">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testId="contact-phone-display" label="Téléphone (affiché)" value={data.phone_display} onChange={(v) => set({ phone_display: v })} placeholder="06 49 07 08 20" />
          <Field testId="contact-phone-e164" label="Téléphone (tel: link)" value={data.phone} onChange={(v) => set({ phone: v })} placeholder="+33649070820" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testId="contact-email" label="Email de contact (visible client)" value={data.email} onChange={(v) => set({ email: v })} placeholder="auvergne.tech.racing@gmail.com" />
          <Field testId="contact-email-sender" label="Email expéditeur (Resend)" value={data.email_sender} onChange={(v) => set({ email_sender: v })} placeholder="contact@auvergnetechracing.com" />
        </div>
        <p className="text-white/40 text-[11px] mt-1">
          💡 L'email visible client reçoit les réponses. L'email expéditeur doit être un domaine vérifié dans Resend (ne pas changer sauf si tu sais ce que tu fais).
        </p>
      </Section>

      <Section title="Adresse">
        <Field testId="contact-street" label="Rue" value={data.street} onChange={(v) => set({ street: v })} placeholder="47 rue Jules Verne" />
        <div className="grid grid-cols-2 gap-3">
          <Field testId="contact-postal-code" label="Code postal" value={data.postal_code} onChange={(v) => set({ postal_code: v })} placeholder="63100" />
          <Field testId="contact-city" label="Ville" value={data.city} onChange={(v) => set({ city: v })} placeholder="Clermont-Ferrand" />
        </div>
        <Field testId="contact-maps-url" label="Lien Google Maps" value={data.maps_url} onChange={(v) => set({ maps_url: v })} placeholder="https://www.google.com/maps/…" />
      </Section>

      <Section title="Horaires">
        <Field
          testId="contact-hours-display"
          label="Horaires (texte affiché)"
          value={data.opening_hours_display}
          onChange={(v) => set({ opening_hours_display: v })}
          placeholder="Lundi - Vendredi : 8h - 12h30 / 13h30 - 18h"
        />
        <p className="text-white/40 text-[11px]">
          Ce texte est utilisé pour l'affichage public. Les <em>horaires détaillés Schema.org</em> (utilisés par Google Maps) sont codés en dur pour l'instant — dis-moi si tu veux pouvoir les modifier aussi.
        </p>
      </Section>
    </>
  );
};

const BannerEditor = ({ data, setData }) => (
  <>
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70 space-y-1.5" data-testid="content-banner-help">
      <p>💡 C&apos;est <strong>le bandeau rouge tout en haut du site</strong>, affiché sur toutes les pages publiques. Quand il est activé, il <strong>remplace le bandeau « Promotion »</strong> en haut de l&apos;accueil (les promos restent visibles dans la section Promotions).</p>
      <p>⏱️ Le <strong>compte à rebours</strong> (« Plus que X jours ») s&apos;ajoute automatiquement si le message ou le lien parle de Flexfuel et que l&apos;offre est en cours. La date de fin de l&apos;offre se règle dans <strong>Page Flexfuel → Offre Flexfuel</strong>.</p>
      <p>Si un visiteur ferme le bandeau, il ne revient que si tu changes le message.</p>
    </div>

    <Section title="Activation & style">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.enabled !== false}
          onChange={(e) => setData({ ...data, enabled: e.target.checked })}
          data-testid="content-banner-enabled"
          className="w-4 h-4 accent-[#FF3B30]"
        />
        <span className="text-white text-sm">Afficher la bannière</span>
      </label>
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.show_countdown !== false}
          onChange={(e) => setData({ ...data, show_countdown: e.target.checked })}
          data-testid="content-banner-show-countdown"
          className="w-4 h-4 accent-[#FF3B30]"
        />
        <span className="text-white text-sm">Afficher le compte à rebours de l&apos;offre Flexfuel</span>
      </label>
      <div>
        <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Style / couleur</label>
        <select
          value={data.style || "red"}
          onChange={(e) => setData({ ...data, style: e.target.value })}
          data-testid="content-banner-style"
          className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
        >
          <option value="red">🔴 Rouge ATR (recommandé)</option>
          <option value="dark">⚫ Sombre</option>
          <option value="gold">🟡 Or</option>
          <option value="green">🟢 Vert</option>
        </select>
      </div>
    </Section>

    <Section title="Contenu">
      <Field label="Message principal" value={data.message} onChange={(v) => setData({ ...data, message: v })} placeholder="🎁 Offre du mois - -15% sur toutes les conversions Flexfuel !" />
      <Field label="Badge de mise en avant (facultatif)" value={data.highlight_text} onChange={(v) => setData({ ...data, highlight_text: v })} placeholder="-15%" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Texte du bouton" value={data.cta_text} onChange={(v) => setData({ ...data, cta_text: v })} placeholder="En profiter" />
        <Field label="Lien du bouton" value={data.cta_url} onChange={(v) => setData({ ...data, cta_url: v })} placeholder="/conversion-flexfuel-e85" />
      </div>
    </Section>
  </>
);

const EmailTemplatesEditor = ({ data, setData }) => {
  const setSection = (key, patch) =>
    setData({ ...data, [key]: { ...(data[key] || {}), ...patch } });
  return (
    <>
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Personnalise les 2 emails automatiques envoyés par ton système. Utilise les variables{" "}
        <code className="text-white/90">{"{client_name}"}</code> et{" "}
        <code className="text-white/90">{"{service_name}"}</code> pour insérer les infos du client dynamiquement.
      </div>

      <Section title="📅 Rappel 24h (envoyé la veille du RDV à 18h)">
        <Field label="Objet de l'email (préfixe)" value={data.reminder_24h?.subject_prefix} onChange={(v) => setSection("reminder_24h", { subject_prefix: v })} placeholder="📅 Rappel — Votre RDV demain" />
        <Field label="Salutation (utilise {client_name})" value={data.reminder_24h?.greeting} onChange={(v) => setSection("reminder_24h", { greeting: v })} placeholder="Bonjour {client_name}," />
        <Field label="Intro (1-2 phrases)" value={data.reminder_24h?.intro} onChange={(v) => setSection("reminder_24h", { intro: v })} multiline placeholder="Nous vous confirmons votre rendez-vous prévu demain..." />
        <Field label="Signature / phrase de fin" value={data.reminder_24h?.closing} onChange={(v) => setSection("reminder_24h", { closing: v })} multiline placeholder="À demain !" />
      </Section>

      <Section title="★ Demande d'avis Google (envoyée 3 jours après un RDV terminé)">
        <Field label="Objet de l'email (préfixe)" value={data.review_request?.subject_prefix} onChange={(v) => setSection("review_request", { subject_prefix: v })} placeholder="★ Un petit avis pour Auvergne Tech Racing" />
        <Field label="Salutation" value={data.review_request?.greeting} onChange={(v) => setSection("review_request", { greeting: v })} placeholder="Bonjour {client_name}," />
        <Field label="Intro (personnalisée avec le service)" value={data.review_request?.intro} onChange={(v) => setSection("review_request", { intro: v })} multiline placeholder="Nous espérons que tout se passe bien... « {service_name} »" />
        <Field label="Demande principale" value={data.review_request?.body_ask} onChange={(v) => setSection("review_request", { body_ask: v })} multiline />
        <Field label="Texte du bouton CTA" value={data.review_request?.cta_text} onChange={(v) => setSection("review_request", { cta_text: v })} placeholder="★ Laisser mon avis Google" />
        <Field label="Signature / phrase de fin" value={data.review_request?.closing} onChange={(v) => setSection("review_request", { closing: v })} multiline />
      </Section>
    </>
  );
};

const HeroBgField = ({ value, onChange }) => (
  <div className="space-y-2">
    <Field
      label="Image de fond du Hero (URL)"
      value={value}
      onChange={onChange}
      placeholder="https://.../image.jpeg"
      testId="content-hero-bg-url"
    />
    {value ? (
      <div className="relative w-full h-32 rounded-md overflow-hidden border border-white/[0.06]">
        <img src={value} alt="Aperçu image de fond" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-transparent pointer-events-none" />
        <span className="absolute bottom-2 left-2 text-white/80 text-xs bg-black/60 px-2 py-1 rounded">Aperçu (avec overlay sombre)</span>
      </div>
    ) : (
      <div className="text-white/40 text-xs italic">Aucune image — le hero utilisera seulement les dégradés rouge/noir par défaut.</div>
    )}
    <div className="text-white/40 text-xs">
      💡 Astuce : la même image que la page principale par défaut. Utilise une URL d&apos;image en haute résolution (ex : Unsplash, ton hébergement).
    </div>
  </div>
);

const HeroEditor = ({ hero, setHero, showBadge = true, showTwoLineTitle = true, showCta = true, showCtaBook = true, showIntroBold = false }) => (
  <Section title="Hero (haut de page)">
    {showBadge && (
      <Field label="Badge (petite étiquette en haut)" value={hero.badge} onChange={(v) => setHero({ ...hero, badge: v })} placeholder="Stage 1 · Stage 2 · Flexfuel" />
    )}
    {showTwoLineTitle ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="H1 ligne 1 (blanc)" value={hero.h1_top} onChange={(v) => setHero({ ...hero, h1_top: v })} placeholder="Titre principal" />
        <Field label="H1 ligne 2 (rouge)" value={hero.h1_highlight} onChange={(v) => setHero({ ...hero, h1_highlight: v })} placeholder="à Clermont-Ferrand" />
      </div>
    ) : null}
    <Field label="Intro (paragraphe)" value={hero.intro} onChange={(v) => setHero({ ...hero, intro: v })} multiline placeholder="Texte d'introduction..." />
    {showIntroBold && (
      <Field label="Phrase en gras à la fin de l'intro" value={hero.intro_bold} onChange={(v) => setHero({ ...hero, intro_bold: v })} placeholder="Vous déposez, on gère." />
    )}
    {showCta && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={hero.cta_call_label !== undefined ? "Libellé bouton téléphone (avec numéro)" : "Libellé bouton téléphone"} value={hero.cta_call_label ?? hero.cta_phone_label} onChange={(v) => setHero(hero.cta_call_label !== undefined ? { ...hero, cta_call_label: v } : { ...hero, cta_phone_label: v })} placeholder="06 49 07 08 20" />
        {showCtaBook && (
          <Field label="Libellé bouton Devis" value={hero.cta_book_label} onChange={(v) => setHero({ ...hero, cta_book_label: v })} placeholder="Devis gratuit" />
        )}
      </div>
    )}
  </Section>
);

const StringListEditor = ({ title, items, setItems, hint, placeholder = "..." }) => {
  const list = items || [];
  const update = (i, v) => {
    const next = [...list];
    next[i] = v;
    setItems(next);
  };
  const remove = (i) => {
    const next = [...list];
    next.splice(i, 1);
    setItems(next);
  };
  const add = () => setItems([...list, ""]);

  return (
    <Section title={`${title} (${list.length})`}>
      {hint && <div className="text-xs text-white/50 mb-2">{hint}</div>}
      {list.map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">
            <Field label={`Ligne ${i + 1}`} value={v} onChange={(nv) => update(i, nv)} multiline placeholder={placeholder} />
          </div>
          <button
            onClick={() => remove(i)}
            className="mt-6 text-red-400 hover:text-red-300 p-1"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button onClick={add} className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5">
        <Plus size={14} /> Ajouter
      </button>
    </Section>
  );
};

const ReprogPageEditor = ({ data, setData }) => {
  const hero = data.hero || {};
  const setHero = (v) => setData({ ...data, hero: v });
  const services = data.services || [];
  const updateService = (i, patch) => {
    const next = [...services];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, services: next });
  };
  const removeService = (i) => {
    const next = [...services];
    next.splice(i, 1);
    setData({ ...data, services: next });
  };
  const addService = () => setData({ ...data, services: [...services, { title: "", description: "", price: "" }] });
  const finalCta = data.final_cta || {};
  const setFinalCta = (v) => setData({ ...data, final_cta: v });

  return (
    <>
      <PageSEOEditor data={data} setData={setData} pageLabel="Optimisation" pageRoute="/reprogrammation-moteur-clermont-ferrand" />
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Contenu de la page <code className="text-white/90">/reprogrammation-moteur-clermont-ferrand</code>. Astuce : dans les avantages, tape <code className="text-white/90">{"{rating}"}</code> et <code className="text-white/90">{"{reviews_count}"}</code> — ils seront remplacés par la note et le nombre d&apos;avis Google en temps réel.
      </div>
      <Section title="Image de fond du Hero">
        <HeroBgField value={data.hero_bg_image_url} onChange={(v) => setData({ ...data, hero_bg_image_url: v })} />
      </Section>
      <HeroEditor hero={hero} setHero={setHero} />

      <Section title="Titres section 'Nos prestations'">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant (blanc)" value={data.services_title_before} onChange={(v) => setData({ ...data, services_title_before: v })} placeholder="Nos prestations" />
          <Field label="Titre highlight (rouge)" value={data.services_title_highlight} onChange={(v) => setData({ ...data, services_title_highlight: v })} placeholder="reprogrammation" />
        </div>
        <Field label="Sous-titre" value={data.services_subtitle} onChange={(v) => setData({ ...data, services_subtitle: v })} multiline />
      </Section>

      <Section title={`Prestations (${services.length})`}>
        {services.map((s, i) => (
          <div key={i} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">Prestation {i + 1}</span>
              <button onClick={() => removeService(i)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
            <Field label="Titre" value={s.title} onChange={(v) => updateService(i, { title: v })} placeholder="Stage 1 — Optimisation moteur" />
            <Field label="Description" value={s.description} onChange={(v) => updateService(i, { description: v })} multiline />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Prix (optionnel)" value={s.price} onChange={(v) => updateService(i, { price: v })} placeholder="À partir de 300€ TTC" />
              <Field label="Lien (optionnel)" value={s.link_href} onChange={(v) => updateService(i, { link_href: v })} placeholder="/conversion-flexfuel-e85" />
              <Field label="Libellé du lien" value={s.link_label} onChange={(v) => updateService(i, { link_label: v })} placeholder="Voir plus" />
            </div>
          </div>
        ))}
        <button onClick={addService} className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5">
          <Plus size={14} /> Ajouter une prestation
        </button>
      </Section>

      <Section title="Titre section 'Pourquoi choisir ATR'">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Avant (blanc)" value={data.advantages_title_before} onChange={(v) => setData({ ...data, advantages_title_before: v })} placeholder="Pourquoi choisir" />
          <Field label="Highlight (rouge)" value={data.advantages_title_highlight} onChange={(v) => setData({ ...data, advantages_title_highlight: v })} placeholder="ATR" />
          <Field label="Après (blanc)" value={data.advantages_title_after} onChange={(v) => setData({ ...data, advantages_title_after: v })} placeholder=" ?" />
        </div>
      </Section>

      <StringListEditor
        title="Avantages"
        items={data.advantages}
        setItems={(items) => setData({ ...data, advantages: items })}
        hint="Utilise {rating} et {reviews_count} pour insérer dynamiquement la note et le nombre d'avis Google."
        placeholder="Un avantage clé..."
      />

      <Section title="Bloc final 'Prendre rendez-vous'">
        <Field label="Titre" value={finalCta.title} onChange={(v) => setFinalCta({ ...finalCta, title: v })} placeholder="Prendre rendez-vous" />
        <Field label="Sous-titre" value={finalCta.subtitle} onChange={(v) => setFinalCta({ ...finalCta, subtitle: v })} multiline />
        <Field label="Libellé bouton final" value={finalCta.button_label} onChange={(v) => setFinalCta({ ...finalCta, button_label: v })} placeholder="Réserver un créneau en ligne" />
      </Section>
    </>
  );
};

const RepEntretienPageEditor = ({ data, setData }) => {
  const hero = data.hero || {};
  const setHero = (v) => setData({ ...data, hero: v });
  const services = data.services || [];
  const updateService = (i, patch) => {
    const next = [...services];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, services: next });
  };
  const removeService = (i) => {
    const next = [...services];
    next.splice(i, 1);
    setData({ ...data, services: next });
  };
  const addService = () => setData({ ...data, services: [...services, { title: "", description: "" }] });
  const brandsSection = data.brands_section || {};
  const setBrandsSection = (v) => setData({ ...data, brands_section: v });
  const finalCta = data.final_cta || {};
  const setFinalCta = (v) => setData({ ...data, final_cta: v });

  return (
    <>
      <PageSEOEditor data={data} setData={setData} pageLabel="Mécanique" pageRoute="/reparation-entretien-clermont-ferrand" />
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Contenu de la page <code className="text-white/90">/reparation-entretien-clermont-ferrand</code>. Utilise <code className="text-white/90">{"{rating}"}</code> et <code className="text-white/90">{"{reviews_count}"}</code> dans les avantages pour insérer la note Google.
      </div>
      <Section title="Image de fond du Hero">
        <HeroBgField value={data.hero_bg_image_url} onChange={(v) => setData({ ...data, hero_bg_image_url: v })} />
      </Section>
      <HeroEditor hero={hero} setHero={setHero} />

      <Section title="Titres section 'Nos prestations'">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant" value={data.services_title_before} onChange={(v) => setData({ ...data, services_title_before: v })} placeholder="Nos prestations" />
          <Field label="Titre highlight (rouge)" value={data.services_title_highlight} onChange={(v) => setData({ ...data, services_title_highlight: v })} placeholder="mécanique" />
        </div>
        <Field label="Sous-titre" value={data.services_subtitle} onChange={(v) => setData({ ...data, services_subtitle: v })} multiline />
      </Section>

      <Section title={`Prestations (${services.length})`}>
        {services.map((s, i) => (
          <div key={i} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">Prestation {i + 1}</span>
              <button onClick={() => removeService(i)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
            <Field label="Titre" value={s.title} onChange={(v) => updateService(i, { title: v })} placeholder="Entretien courant" />
            <Field label="Description" value={s.description} onChange={(v) => updateService(i, { description: v })} multiline />
          </div>
        ))}
        <button onClick={addService} className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5">
          <Plus size={14} /> Ajouter une prestation
        </button>
      </Section>

      <Section title="Section 'Spécialité véhicules allemands'">
        <Field label="Badge" value={brandsSection.badge} onChange={(v) => setBrandsSection({ ...brandsSection, badge: v })} placeholder="Notre spécialité" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant" value={brandsSection.title_top} onChange={(v) => setBrandsSection({ ...brandsSection, title_top: v })} placeholder="Spécialité :" />
          <Field label="Titre highlight (rouge)" value={brandsSection.title_highlight} onChange={(v) => setBrandsSection({ ...brandsSection, title_highlight: v })} placeholder="véhicules allemands" />
        </div>
        <Field label="Description" value={brandsSection.description} onChange={(v) => setBrandsSection({ ...brandsSection, description: v })} multiline />
        <Field label="Sous-titre des marques" value={brandsSection.brands_subtitle} onChange={(v) => setBrandsSection({ ...brandsSection, brands_subtitle: v })} placeholder="Marques maîtrisées" />
        <Field
          label="Liste des marques (séparées par des virgules)"
          value={(brandsSection.brands || []).join(", ")}
          onChange={(v) => setBrandsSection({ ...brandsSection, brands: v.split(",").map((s) => s.trim()).filter(Boolean) })}
          placeholder="Audi, BMW, Mercedes-Benz, Volkswagen, Porsche"
        />
        <Field label="Paragraphe final" value={brandsSection.outro} onChange={(v) => setBrandsSection({ ...brandsSection, outro: v })} multiline />
      </Section>

      <Section title="Titre section 'Pourquoi choisir ATR'">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Avant" value={data.advantages_title_before} onChange={(v) => setData({ ...data, advantages_title_before: v })} placeholder="Pourquoi choisir" />
          <Field label="Highlight (rouge)" value={data.advantages_title_highlight} onChange={(v) => setData({ ...data, advantages_title_highlight: v })} placeholder="ATR" />
          <Field label="Après" value={data.advantages_title_after} onChange={(v) => setData({ ...data, advantages_title_after: v })} placeholder=" pour votre mécanique ?" />
        </div>
      </Section>

      <StringListEditor
        title="Avantages"
        items={data.advantages}
        setItems={(items) => setData({ ...data, advantages: items })}
        hint="Utilise {rating} et {reviews_count} pour insérer la note Google."
      />

      <Section title="Bloc final 'Prendre rendez-vous'">
        <Field label="Titre" value={finalCta.title} onChange={(v) => setFinalCta({ ...finalCta, title: v })} placeholder="Prendre rendez-vous" />
        <Field label="Sous-titre" value={finalCta.subtitle} onChange={(v) => setFinalCta({ ...finalCta, subtitle: v })} multiline />
        <Field label="Libellé bouton final" value={finalCta.button_label} onChange={(v) => setFinalCta({ ...finalCta, button_label: v })} placeholder="Réserver un créneau en ligne" />
      </Section>
    </>
  );
};

const StepsEditor = ({ title, steps, setSteps }) => {
  const list = steps || [];
  const update = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    setSteps(next);
  };
  const remove = (i) => {
    const next = [...list];
    next.splice(i, 1);
    setSteps(next);
  };
  const add = () => setSteps([...list, { title: "", text: "" }]);

  return (
    <div className="space-y-2">
      <label className="block text-white/50 text-xs font-medium">{title}</label>
      {list.map((s, i) => (
        <div key={i} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Étape {i + 1}</span>
            <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
          </div>
          <Field label="Titre" value={s.title} onChange={(v) => update(i, { title: v })} />
          <Field label="Texte" value={s.text} onChange={(v) => update(i, { text: v })} multiline />
        </div>
      ))}
      <button onClick={add} className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5">
        <Plus size={14} /> Ajouter une étape
      </button>
    </div>
  );
};

const AchatVentePageEditor = ({ data, setData }) => {
  const hero = data.hero || {};
  const setHero = (v) => setData({ ...data, hero: v });
  const sellSection = data.sell_section || {};
  const setSellSection = (v) => setData({ ...data, sell_section: v });
  const searchSection = data.search_section || {};
  const setSearchSection = (v) => setData({ ...data, search_section: v });
  const finalCta = data.final_cta || {};
  const setFinalCta = (v) => setData({ ...data, final_cta: v });

  return (
    <>
      <PageSEOEditor data={data} setData={setData} pageLabel="Dépôt-Vente" pageRoute="/achat-vente-voiture-clermont-ferrand" />
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Contenu de la page <code className="text-white/90">/achat-vente-voiture-clermont-ferrand</code>. Les annonces véhicules sont dans <strong>Dépôt-Vente — Véhicules</strong>, cette page-ci gère les textes autour.
      </div>

      <Section title="Image de fond du Hero">
        <HeroBgField value={data.hero_bg_image_url} onChange={(v) => setData({ ...data, hero_bg_image_url: v })} />
      </Section>

      <HeroEditor hero={hero} setHero={setHero} showCtaBook={false} showIntroBold />

      <Section title="Section 'Dépôt-Vente' (Service 1 — Vendre)">
        <Field label="Badge" value={sellSection.badge} onChange={(v) => setSellSection({ ...sellSection, badge: v })} placeholder="Service 1 — Vendre" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant" value={sellSection.title_top} onChange={(v) => setSellSection({ ...sellSection, title_top: v })} />
          <Field label="Titre highlight (rouge)" value={sellSection.title_highlight} onChange={(v) => setSellSection({ ...sellSection, title_highlight: v })} />
        </div>
        <Field label="Description" value={sellSection.description} onChange={(v) => setSellSection({ ...sellSection, description: v })} multiline />
        <Field label="Titre des étapes" value={sellSection.steps_title} onChange={(v) => setSellSection({ ...sellSection, steps_title: v })} placeholder="Comment ça marche" />
        <StepsEditor title="Étapes de vente" steps={sellSection.steps} setSteps={(steps) => setSellSection({ ...sellSection, steps })} />
      </Section>

      <Section title="Section 'Recherche véhicule' (Service 2 — Acheter)">
        <Field label="Badge" value={searchSection.badge} onChange={(v) => setSearchSection({ ...searchSection, badge: v })} placeholder="Service 2 — Acheter" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant" value={searchSection.title_top} onChange={(v) => setSearchSection({ ...searchSection, title_top: v })} />
          <Field label="Titre highlight (rouge)" value={searchSection.title_highlight} onChange={(v) => setSearchSection({ ...searchSection, title_highlight: v })} />
        </div>
        <Field label="Description" value={searchSection.description} onChange={(v) => setSearchSection({ ...searchSection, description: v })} multiline />
        <Field label="Titre des étapes" value={searchSection.steps_title} onChange={(v) => setSearchSection({ ...searchSection, steps_title: v })} />
        <StepsEditor title="Étapes de recherche" steps={searchSection.steps} setSteps={(steps) => setSearchSection({ ...searchSection, steps })} />
      </Section>

      <Section title="Titre section 'Pourquoi confier à ATR'">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Avant" value={data.advantages_title_before} onChange={(v) => setData({ ...data, advantages_title_before: v })} />
          <Field label="Highlight (rouge)" value={data.advantages_title_highlight} onChange={(v) => setData({ ...data, advantages_title_highlight: v })} placeholder="ATR" />
          <Field label="Après" value={data.advantages_title_after} onChange={(v) => setData({ ...data, advantages_title_after: v })} placeholder=" ?" />
        </div>
      </Section>

      <StringListEditor
        title="Avantages"
        items={data.advantages}
        setItems={(items) => setData({ ...data, advantages: items })}
      />

      <Section title="Bloc final 'Estimation gratuite'">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Titre avant" value={finalCta.title_before} onChange={(v) => setFinalCta({ ...finalCta, title_before: v })} placeholder="Demandez votre" />
          <Field label="Titre highlight (rouge)" value={finalCta.title_highlight} onChange={(v) => setFinalCta({ ...finalCta, title_highlight: v })} placeholder="estimation gratuite" />
        </div>
        <Field label="Sous-titre" value={finalCta.subtitle} onChange={(v) => setFinalCta({ ...finalCta, subtitle: v })} multiline />
        <Field label="Libellé du bouton" value={finalCta.button_label} onChange={(v) => setFinalCta({ ...finalCta, button_label: v })} placeholder="Nous contacter — 06 49 07 08 20" />
      </Section>
    </>
  );
};

const SortableNavItem = ({
  item,
  idx,
  opts,
  itemsLength,
  groupOptions,
  updateItem,
  moveItem,
  toggleVisible,
  removeItem,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  const isGroup = item.type === "group";
  const isChild = !!opts.child;
  const isOrphan = !!opts.orphan;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      data-testid={`content-nav-item-${idx}`}
      className={`border rounded-md p-3 space-y-2 ${
        item.visible === false
          ? "border-white/[0.04] bg-[#1C1C1E]/50 opacity-60"
          : isGroup
          ? "border-[#FF3B30]/30 bg-[#FF3B30]/[0.04]"
          : isChild
          ? "border-white/[0.04] bg-[#1C1C1E] ml-6 border-l-2 border-l-[#FF3B30]/30"
          : isOrphan
          ? "border-yellow-500/30 bg-yellow-500/[0.04]"
          : "border-white/[0.04] bg-[#1C1C1E]"
      } ${isDragging ? "ring-2 ring-[#FF3B30] shadow-2xl" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            type="button"
            {...listeners}
            data-testid={`content-nav-drag-${idx}`}
            className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white flex-shrink-0 touch-none p-0.5 -m-0.5 rounded hover:bg-white/[0.06]"
            title="Glisser pour réordonner"
            aria-label="Glisser pour réordonner"
          >
            <GripVertical size={14} />
          </button>
          <span className={`text-xs font-semibold truncate ${isGroup ? "text-[#FF3B30]" : item.style === "highlight" ? "text-[#FF3B30]" : "text-white/70"}`}>
            {idx + 1}. {isGroup ? "📂 " : isChild ? "↳ " : ""}
            {item.label || "(sans libellé)"}
            {isGroup && <span className="ml-2 text-[10px] text-white/40 font-normal uppercase tracking-wider">menu déroulant</span>}
            {isOrphan && <span className="ml-2 text-[10px] text-yellow-400 font-normal uppercase tracking-wider">⚠ parent manquant</span>}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => moveItem(idx, -1)}
            disabled={idx === 0}
            data-testid={`content-nav-up-${idx}`}
            className="text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1"
            title="Monter"
          >
            <ArrowUp size={13} />
          </button>
          <button
            onClick={() => moveItem(idx, 1)}
            disabled={idx === itemsLength - 1}
            data-testid={`content-nav-down-${idx}`}
            className="text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1"
            title="Descendre"
          >
            <ArrowDown size={13} />
          </button>
          <button
            onClick={() => toggleVisible(idx)}
            data-testid={`content-nav-toggle-${idx}`}
            className="text-white/50 hover:text-white p-1"
            title={item.visible === false ? "Afficher" : "Masquer"}
          >
            {item.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
          <button
            onClick={() => removeItem(idx)}
            data-testid={`content-nav-delete-${idx}`}
            className="text-red-400 hover:text-red-300 p-1"
            title={isGroup ? "Supprimer le menu (ses sous-liens deviendront des liens simples)" : "Supprimer"}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Libellé" value={item.label} onChange={(v) => updateItem(idx, { label: v })} placeholder="Services" testId={`content-nav-label-${idx}`} />
        {!isGroup && <Field label="Lien" value={item.href} onChange={(v) => updateItem(idx, { href: v })} placeholder="/#booking" testId={`content-nav-href-${idx}`} />}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-white/50 text-xs font-medium mb-1.5">Type</label>
          <select
            data-testid={`content-nav-type-${idx}`}
            value={item.type || "link"}
            onChange={(e) => updateItem(idx, { type: e.target.value, ...(e.target.value === "group" ? { parent_id: "", href: "" } : {}) })}
            className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
          >
            <option value="link">Lien simple</option>
            <option value="group">Menu déroulant</option>
          </select>
        </div>
        <div>
          <label className="block text-white/50 text-xs font-medium mb-1.5">Style</label>
          <select
            data-testid={`content-nav-style-${idx}`}
            value={item.style || "normal"}
            onChange={(e) => updateItem(idx, { style: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
          >
            <option value="normal">Normal (blanc)</option>
            <option value="highlight">Mis en avant (rouge)</option>
          </select>
        </div>
      </div>

      {!isGroup && groupOptions.length > 0 && (
        <div>
          <label className="block text-white/50 text-xs font-medium mb-1.5">
            Rattaché au menu déroulant
            {isChild && <span className="ml-1 text-[10px] text-white/40 normal-case">(retire pour remettre au top-level)</span>}
          </label>
          <select
            data-testid={`content-nav-parent-${idx}`}
            value={item.parent_id || ""}
            onChange={(e) => updateItem(idx, { parent_id: e.target.value || undefined })}
            className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
          >
            <option value="">— Aucun (top-level dans la navbar)</option>
            {groupOptions.filter((g) => g.id !== item.id).map((g) => (
              <option key={g.id} value={g.id}>📂 {g.label}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const NavbarEditor = ({ data, setData }) => {
  const items = data.items || [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setData({ ...data, items: arrayMove(items, oldIdx, newIdx) });
  };

  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    setData({ ...data, items: next });
  };

  const removeItem = (idx) => {
    const removed = items[idx];
    const next = [...items];
    next.splice(idx, 1);
    // If we removed a group, detach its children (set their parent_id to empty)
    if (removed?.type === "group" && removed?.id) {
      next.forEach((it) => {
        if (it.parent_id === removed.id) delete it.parent_id;
      });
    }
    setData({ ...data, items: next });
  };

  const moveItem = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setData({ ...data, items: next });
  };

  const addItem = () =>
    setData({
      ...data,
      items: [
        ...items,
        {
          id: `custom-${Date.now()}`,
          label: "Nouveau lien",
          href: "/",
          style: "normal",
          visible: true,
        },
      ],
    });

  const addGroup = () =>
    setData({
      ...data,
      items: [
        ...items,
        {
          id: `group-${Date.now()}`,
          label: "Nouveau menu",
          type: "group",
          href: "",
          style: "normal",
          visible: true,
        },
      ],
    });

  // Adds a NEW child link INSIDE the given group.
  // Inserts it in the flat array right after the last existing sibling of the same group
  // so physical order matches visual hierarchy (up/down arrows behave sensibly).
  const addChildTo = (groupId, groupLabel) => {
    const newItem = {
      id: `sub-${Date.now()}`,
      label: `Nouveau lien${groupLabel ? ` (${groupLabel})` : ""}`,
      href: "/",
      style: "highlight",
      visible: true,
      parent_id: groupId,
    };
    // Find last index of a child of this group; if none, insert right after the group itself
    let insertAt = items.findIndex((i) => i.id === groupId);
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].parent_id === groupId) {
        insertAt = i;
        break;
      }
    }
    const next = [...items];
    next.splice(insertAt + 1, 0, newItem);
    setData({ ...data, items: next });
  };

  const toggleVisible = (idx) => updateItem(idx, { visible: !items[idx].visible });

  // Options pour le dropdown "sous-menu de" : uniquement les items type=group
  const groupOptions = items.filter((i) => i.type === "group" && (i.label || i.id));

  // Build hierarchical render order:
  //   [top-level, ...its children, "+ Ajouter dans ce menu", next top-level, ...]
  // Also inserts a divider before orphaned items (children whose parent no longer exists).
  const renderPlan = [];
  const seen = new Set();
  const groupIdSet = new Set(groupOptions.map((g) => g.id));

  items.forEach((item, idx) => {
    if (item.parent_id && groupIdSet.has(item.parent_id)) return; // skip: rendered under parent
    renderPlan.push({ kind: "item", idx });
    seen.add(idx);
    if (item.type === "group") {
      // Attach this group's children in their original flat-array order
      items.forEach((child, cidx) => {
        if (child.parent_id === item.id) {
          renderPlan.push({ kind: "item", idx: cidx, child: true });
          seen.add(cidx);
        }
      });
      renderPlan.push({ kind: "addChild", groupId: item.id, groupLabel: item.label });
    }
  });
  // Orphans (parent_id refers to a group that no longer exists)
  items.forEach((item, idx) => {
    if (seen.has(idx)) return;
    renderPlan.push({ kind: "item", idx, orphan: true });
  });

  const renderItem = (item, idx, opts = {}) => {
    return (
      <SortableNavItem
        key={item.id || idx}
        item={item}
        idx={idx}
        opts={opts}
        itemsLength={items.length}
        groupOptions={groupOptions}
        updateItem={updateItem}
        moveItem={moveItem}
        toggleVisible={toggleVisible}
        removeItem={removeItem}
      />
    );
  };

  return (
    <>
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70 space-y-1">
        <div>💡 Édite le menu de navigation : <strong>libellés, liens, ordre, visibilité, style, sous-menus</strong>.</div>
        <div className="text-white/50">
          <strong className="text-[#FF3B30]">📂 Menu déroulant</strong> = un titre qui ouvre un sous-menu au survol.
          Pour <strong>ajouter un lien dans un menu déroulant</strong>, utilise le bouton <strong className="text-[#FF3B30]">« + Ajouter dans "…" »</strong> qui apparaît juste sous chaque menu.
        </div>
        <div className="text-white/40 text-[11px] mt-1">
          🖱️ <strong>Glisser-déposer</strong> : attrape la poignée <span className="inline-block align-middle"><GripVertical size={11} className="inline text-white/60" /></span> à gauche de chaque item pour le réordonner. Pour déplacer un lien vers un autre menu déroulant, utilise le champ <em>« Rattaché au menu déroulant »</em>.
        </div>
      </div>

      <Section title="Bouton RDV (à droite du menu)">
        <Field
          label="Libellé du bouton principal"
          value={data.book_button_label}
          onChange={(v) => setData({ ...data, book_button_label: v })}
          placeholder="RDV"
        />
      </Section>

      <Section title={`Éléments du menu (${items.length})`}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {renderPlan.map((entry) => {
              if (entry.kind === "addChild") {
                return (
                  <div key={`addchild-${entry.groupId}`} className="ml-6 -mt-1">
                    <button
                      data-testid={`content-nav-add-child-${entry.groupId}`}
                      onClick={() => addChildTo(entry.groupId, entry.groupLabel)}
                      className="w-full text-left text-[#FF3B30] hover:text-white bg-[#FF3B30]/[0.05] hover:bg-[#FF3B30] transition-colors text-xs font-semibold py-2 px-3 rounded-md border border-dashed border-[#FF3B30]/30 hover:border-[#FF3B30] flex items-center gap-2"
                    >
                      <Plus size={13} /> Ajouter un lien dans « {entry.groupLabel || "menu déroulant"} »
                    </button>
                  </div>
                );
              }
              const item = items[entry.idx];
              if (!item) return null;
              return renderItem(item, entry.idx, { child: entry.child, orphan: entry.orphan });
            })}
          </SortableContext>
        </DndContext>

        <div className="flex gap-2 pt-2 border-t border-white/[0.04]">
          <button
            data-testid="content-nav-add"
            onClick={addItem}
            className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
          >
            <Plus size={14} /> Ajouter un lien top-level
          </button>
          <button
            data-testid="content-nav-add-group"
            onClick={addGroup}
            className="text-white/70 hover:text-white text-sm font-medium flex items-center gap-1.5"
          >
            <Plus size={14} /> Ajouter un menu déroulant
          </button>
        </div>
      </Section>
    </>
  );
};

const DischargeEditor = ({ data, setData }) => {
  const setLabel = (k, v) => setData({ ...data, labels: { ...(data.labels || {}), [k]: v } });
  const engagements = data.engagements || [];
  const updateEng = (idx, patch) => {
    const next = [...engagements];
    next[idx] = { ...next[idx], ...patch };
    setData({ ...data, engagements: next });
  };
  const removeEng = (idx) => {
    const next = [...engagements];
    next.splice(idx, 1);
    setData({ ...data, engagements: next });
  };
  const addEng = () => setData({ ...data, engagements: [...engagements, { title: "", text: "" }] });

  return (
    <>
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Cette page contrôle le contenu du <strong>PDF de décharge de responsabilité</strong> généré depuis l&apos;onglet Décharges. Chaque champ ci-dessous est reflété dans le document imprimé/signé.
      </div>

      <Section title="En-tête du document">
        <Field testId="content-discharge-company-name" label="Nom de l'entreprise (grand titre rouge)" value={data.company_name} onChange={(v) => setData({ ...data, company_name: v })} placeholder="AUVERGNE TECH RACING" />
        <Field testId="content-discharge-doc-title" label="Titre du document (sous le nom)" value={data.document_title} onChange={(v) => setData({ ...data, document_title: v })} placeholder="DÉCHARGE DE RESPONSABILITÉ ET D'INFORMATION PRÉALABLE" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Adresse (encadré gris)" value={data.company_address} onChange={(v) => setData({ ...data, company_address: v })} placeholder="47 rue Jules Verne, 63100 Clermont-Ferrand" />
          <Field label="SIREN / mention légale" value={data.company_siren} onChange={(v) => setData({ ...data, company_siren: v })} placeholder="SIREN : 914 749 676" />
        </div>
      </Section>

      <Section title="Libellés & sections" defaultOpen={false}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Encadré CLIENT" value={data.labels?.client} onChange={(v) => setLabel("client", v)} placeholder="CLIENT" />
          <Field label="Encadré VÉHICULE" value={data.labels?.vehicle} onChange={(v) => setLabel("vehicle", v)} placeholder="VÉHICULE" />
        </div>
        <Field label="Titre section intervention" value={data.labels?.intervention} onChange={(v) => setLabel("intervention", v)} placeholder="NATURE DE L'INTERVENTION" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Case à cocher 1" value={data.labels?.intervention_gain} onChange={(v) => setLabel("intervention_gain", v)} placeholder="Gain de puissance" />
          <Field label="Case à cocher 2" value={data.labels?.intervention_ethanol} onChange={(v) => setLabel("intervention_ethanol", v)} placeholder="Conversion éthanol" />
          <Field label="Case à cocher 3" value={data.labels?.intervention_other} onChange={(v) => setLabel("intervention_other", v)} placeholder="Autres" />
        </div>
      </Section>

      <Section title="Engagements & reconnaissance">
        <Field label="Titre bloc engagements" value={data.labels?.engagements_title} onChange={(v) => setLabel("engagements_title", v)} placeholder="ENGAGEMENTS ET RECONNAISSANCE DU CLIENT" />
        <Field label="Phrase d'introduction (au-dessus des puces)" value={data.labels?.engagements_intro} onChange={(v) => setLabel("engagements_intro", v)} multiline placeholder="Le client déclare avoir été informé..." />

        {engagements.map((eng, idx) => (
          <div key={idx} data-testid={`content-discharge-eng-${idx}`} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">Engagement {idx + 1}</span>
              <button
                onClick={() => removeEng(idx)}
                data-testid={`content-discharge-eng-delete-${idx}`}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <Field label="Titre" value={eng.title} onChange={(v) => updateEng(idx, { title: v })} placeholder="Homologation" />
            <Field label="Texte" value={eng.text} onChange={(v) => updateEng(idx, { text: v })} multiline placeholder="Description de l'engagement..." />
          </div>
        ))}
        <button
          data-testid="content-discharge-eng-add"
          onClick={addEng}
          className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
        >
          <Plus size={14} /> Ajouter un engagement
        </button>
      </Section>

      <Section title="Signature & pied de page" defaultOpen={false}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Ville pour la signature" value={data.signature_city} onChange={(v) => setData({ ...data, signature_city: v })} placeholder="Clermont-Ferrand" />
          <Field label="Ligne 'Fait à ... le ...' — variables {city} et {date}" value={data.labels?.signature_line} onChange={(v) => setLabel("signature_line", v)} placeholder="Fait à {city}, le {date}" />
        </div>
        <Field label="Mention 'Fait en X exemplaires'" value={data.labels?.signature_copies} onChange={(v) => setLabel("signature_copies", v)} placeholder="Fait en deux exemplaires originaux." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Libellé signature client" value={data.labels?.signature_client} onChange={(v) => setLabel("signature_client", v)} placeholder="Signature du client" />
          <Field label="Libellé signature Prestataire" value={data.labels?.signature_provider} onChange={(v) => setLabel("signature_provider", v)} placeholder="Signature du Prestataire" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Mention manuscrite" value={data.labels?.signature_mention} onChange={(v) => setLabel("signature_mention", v)} placeholder={'"Lu et approuvé, bon pour accord"'} />
          <Field label="Mention 'Cachet'" value={data.labels?.signature_stamp} onChange={(v) => setLabel("signature_stamp", v)} placeholder="(Cachet)" />
        </div>
        <Field label="Tampon 'SIGNÉ' (une fois la décharge signée)" value={data.labels?.signed_stamp} onChange={(v) => setLabel("signed_stamp", v)} placeholder="SIGNÉ ✓" />
      </Section>
    </>
  );
};

const FlexfuelPageEditor = ({ data, setData }) => (
  <>
    <FlexfuelOfferSettings />
    <PageSEOEditor data={data} setData={setData} pageLabel="Flexfuel" pageRoute="/conversion-flexfuel-e85" />
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
      💡 Cette page dédiée <code className="text-white/90">/conversion-flexfuel-e85</code> est ta <strong>landing SEO ciblée mots-clés</strong>. Chaque bloc est un signal fort pour Google.
    </div>

    <Section title="Hero — En-tête de page">
      <Field label="Badge (petit texte rouge)" value={data.hero?.badge} onChange={(v) => setData({ ...data, hero: { ...data.hero, badge: v } })} />
      <Field label="H1 — ligne principale" value={data.hero?.h1_line_1} onChange={(v) => setData({ ...data, hero: { ...data.hero, h1_line_1: v } })} />
      <Field label="H1 — segment mis en avant (en rouge, ex: '— {prix_flexfuel}')" value={data.hero?.h1_highlight} onChange={(v) => setData({ ...data, hero: { ...data.hero, h1_highlight: v } })} />
      <Field label="Sous-titre" value={data.hero?.subtitle} onChange={(v) => setData({ ...data, hero: { ...data.hero, subtitle: v } })} multiline />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Bouton téléphone (label affiché)" value={data.hero?.cta_phone_label} onChange={(v) => setData({ ...data, hero: { ...data.hero, cta_phone_label: v } })} />
        <Field label="Bouton devis (label)" value={data.hero?.cta_book} onChange={(v) => setData({ ...data, hero: { ...data.hero, cta_book: v } })} />
      </div>
    </Section>

    <Section title="Bloc Bénéfices — Titre">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Avant" value={data.benefits_title_before} onChange={(v) => setData({ ...data, benefits_title_before: v })} placeholder="Pourquoi passer au" />
        <Field label="Mot en rouge" value={data.benefits_title_highlight} onChange={(v) => setData({ ...data, benefits_title_highlight: v })} placeholder="Flexfuel E85" />
        <Field label="Après (avec ?)" value={data.benefits_title_after} onChange={(v) => setData({ ...data, benefits_title_after: v })} placeholder=" ?" />
      </div>
    </Section>

    <Section title="Bloc Bénéfices — 3 cartes">
      {(data.benefits || []).map((b, idx) => (
        <div key={idx} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Carte {idx + 1}</span>
            <button
              onClick={() => {
                const benefits = [...(data.benefits || [])];
                benefits.splice(idx, 1);
                setData({ ...data, benefits });
              }}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Icône</label>
              <select
                value={b.icon || "fuel"}
                onChange={(e) => {
                  const benefits = [...(data.benefits || [])];
                  benefits[idx] = { ...benefits[idx], icon: e.target.value };
                  setData({ ...data, benefits });
                }}
                className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
              >
                <option value="trending-down">📉 Économies (trending-down)</option>
                <option value="shield-check">🛡️ Certification (shield-check)</option>
                <option value="fuel">⛽ Carburant (fuel)</option>
              </select>
            </div>
            <Field label="Titre" value={b.title} onChange={(v) => {
              const benefits = [...(data.benefits || [])];
              benefits[idx] = { ...benefits[idx], title: v };
              setData({ ...data, benefits });
            }} />
          </div>
          <Field label="Description" value={b.description} onChange={(v) => {
            const benefits = [...(data.benefits || [])];
            benefits[idx] = { ...benefits[idx], description: v };
            setData({ ...data, benefits });
          }} multiline />
        </div>
      ))}
      <button
        onClick={() => setData({ ...data, benefits: [...(data.benefits || []), { icon: "fuel", title: "", description: "" }] })}
        className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
      >
        <Plus size={14} /> Ajouter une carte
      </button>
    </Section>

    <Section title="Articles éditoriaux (SEO)">
      <div className="bg-white/[0.02] border border-white/[0.04] rounded-md p-3 text-xs text-white/50">
        Les articles enrichissent la page pour le SEO. Le dernier peut avoir 2 boutons CTA (RDV + Google Maps).
      </div>
      {(data.articles || []).map((a, idx) => (
        <div key={idx} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Article {idx + 1}</span>
            <button
              onClick={() => {
                const articles = [...(data.articles || [])];
                articles.splice(idx, 1);
                setData({ ...data, articles });
              }}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <Field label="Titre H2" value={a.title} onChange={(v) => {
            const articles = [...(data.articles || [])];
            articles[idx] = { ...articles[idx], title: v };
            setData({ ...data, articles });
          }} />
          <Field label="Paragraphe" value={a.paragraph} onChange={(v) => {
            const articles = [...(data.articles || [])];
            articles[idx] = { ...articles[idx], paragraph: v };
            setData({ ...data, articles });
          }} multiline />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Bouton principal (facultatif — RDV)" value={a.cta_primary} onChange={(v) => {
              const articles = [...(data.articles || [])];
              articles[idx] = { ...articles[idx], cta_primary: v };
              setData({ ...data, articles });
            }} />
            <Field label="Bouton secondaire (facultatif — Maps)" value={a.cta_secondary} onChange={(v) => {
              const articles = [...(data.articles || [])];
              articles[idx] = { ...articles[idx], cta_secondary: v };
              setData({ ...data, articles });
            }} />
          </div>
        </div>
      ))}
      <button
        onClick={() => setData({ ...data, articles: [...(data.articles || []), { title: "", paragraph: "" }] })}
        className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
      >
        <Plus size={14} /> Ajouter un article
      </button>
    </Section>
  </>
);

const HomeLandingEditor = ({ data, setData }) => (
  <>
    <Section title="Sections de l'accueil (ordre & affichage)">
      <HomeSectionsEditor data={data} setData={setData} />
    </Section>
    <PageSEOEditor data={data} setData={setData} pageLabel="Accueil" pageRoute="/" />
    <Section title="Hero (écran d'accueil)">
      <Field label="Badge (petit texte rouge)" value={data.hero?.badge} onChange={(v) => setData({ ...data, hero: { ...data.hero, badge: v } })} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Titre ligne 1" value={data.hero?.title_line_1} onChange={(v) => setData({ ...data, hero: { ...data.hero, title_line_1: v } })} />
        <Field label="Titre ligne 2 (en rouge)" value={data.hero?.title_line_2} onChange={(v) => setData({ ...data, hero: { ...data.hero, title_line_2: v } })} />
      </div>
      <Field label="Sous-titre" value={data.hero?.subtitle} onChange={(v) => setData({ ...data, hero: { ...data.hero, subtitle: v } })} multiline />
      <Field label="Texte du bouton principal" value={data.hero?.cta_primary} onChange={(v) => setData({ ...data, hero: { ...data.hero, cta_primary: v } })} />
    </Section>

    <Section title="Section Expertise & Rapidité">
      <Field label="Badge (petit texte rouge)" value={data.expertise?.badge} onChange={(v) => setData({ ...data, expertise: { ...data.expertise, badge: v } })} />
      <Field label="Titre" value={data.expertise?.title} onChange={(v) => setData({ ...data, expertise: { ...data.expertise, title: v } })} />
      <Field label="Sous-titre (optionnel)" value={data.expertise?.subtitle} onChange={(v) => setData({ ...data, expertise: { ...data.expertise, subtitle: v } })} multiline />
    </Section>

    <ServicesCardsEditor />

    <Section title="FAQ Flexfuel">
      <Field label="Badge" value={data.flexfuel_faq?.badge} onChange={(v) => setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, badge: v } })} />
      <Field label="Titre" value={data.flexfuel_faq?.title} onChange={(v) => setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, title: v } })} />
      {(data.flexfuel_faq?.items || []).map((item, idx) => (
        <div key={idx} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Question {idx + 1}</span>
            <button
              data-testid={`content-faq-delete-${idx}`}
              onClick={() => {
                const items = [...data.flexfuel_faq.items];
                items.splice(idx, 1);
                setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, items } });
              }}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <Field label="Question" value={item.q} onChange={(v) => {
            const items = [...data.flexfuel_faq.items];
            items[idx] = { ...items[idx], q: v };
            setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, items } });
          }} />
          <Field label="Réponse" value={item.a} onChange={(v) => {
            const items = [...data.flexfuel_faq.items];
            items[idx] = { ...items[idx], a: v };
            setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, items } });
          }} multiline />
        </div>
      ))}
      <button
        data-testid="content-faq-add"
        onClick={() => {
          const items = [...(data.flexfuel_faq?.items || []), { q: "", a: "" }];
          setData({ ...data, flexfuel_faq: { ...data.flexfuel_faq, items } });
        }}
        className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
      >
        <Plus size={14} /> Ajouter une question
      </button>
    </Section>

    <Section title="Section SEO éditoriale (H1 + 4 H2)" defaultOpen={false}>
      <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-md p-3 text-xs text-white/70">
        💡 Cette section apparaît au-dessus du footer. Elle est <strong>cruciale pour le SEO</strong> — n&apos;hésite pas à y placer tes mots-clés naturellement.
      </div>
      <Field label="Sur-titre (petit texte rouge)" value={data.seo_editorial?.eyebrow} onChange={(v) => setData({ ...data, seo_editorial: { ...data.seo_editorial, eyebrow: v } })} testId="content-seo-eyebrow" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="H1 — ligne 1" value={data.seo_editorial?.h1_line_1} onChange={(v) => setData({ ...data, seo_editorial: { ...data.seo_editorial, h1_line_1: v } })} testId="content-seo-h1-1" />
        <Field label="H1 — ligne 2 (en gris)" value={data.seo_editorial?.h1_line_2} onChange={(v) => setData({ ...data, seo_editorial: { ...data.seo_editorial, h1_line_2: v } })} testId="content-seo-h1-2" />
      </div>
      {(data.seo_editorial?.sections || []).map((s, idx) => (
        <div key={idx} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <span className="text-white/40 text-xs">Section {idx + 1}{idx === 3 ? " (auto-injecte la note Google + nombre d'avis)" : ""}</span>
          <Field label="Titre H2" value={s.title} onChange={(v) => {
            const sections = [...(data.seo_editorial?.sections || [])];
            sections[idx] = { ...sections[idx], title: v };
            setData({ ...data, seo_editorial: { ...data.seo_editorial, sections } });
          }} />
          <Field label="Paragraphe" value={s.paragraph} onChange={(v) => {
            const sections = [...(data.seo_editorial?.sections || [])];
            sections[idx] = { ...sections[idx], paragraph: v };
            setData({ ...data, seo_editorial: { ...data.seo_editorial, sections } });
          }} multiline />
        </div>
      ))}
    </Section>

    <Section title="Section Promotions" defaultOpen={false}>
      <Field label="Badge (petit texte rouge)" value={data.promotions?.badge} onChange={(v) => setData({ ...data, promotions: { ...data.promotions, badge: v } })} />
      <Field label="Titre" value={data.promotions?.title} onChange={(v) => setData({ ...data, promotions: { ...data.promotions, title: v } })} />
    </Section>

    <Section title="Calculateur Flexfuel" defaultOpen={false}>
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.flexfuel_calculator?.show_animation !== false}
          onChange={(e) => setData({ ...data, flexfuel_calculator: { ...data.flexfuel_calculator, show_animation: e.target.checked } })}
          data-testid="content-calc-show-animation"
          className="w-4 h-4 accent-[#FF3B30]"
        />
        <span className="text-white text-sm">Afficher l&apos;animation Flexfuel</span>
      </label>
      <Field label="Badge" value={data.flexfuel_calculator?.badge} onChange={(v) => setData({ ...data, flexfuel_calculator: { ...data.flexfuel_calculator, badge: v } })} />
      <Field label="Titre" value={data.flexfuel_calculator?.title} onChange={(v) => setData({ ...data, flexfuel_calculator: { ...data.flexfuel_calculator, title: v } })} />
      <Field label="Sous-titre" value={data.flexfuel_calculator?.subtitle} onChange={(v) => setData({ ...data, flexfuel_calculator: { ...data.flexfuel_calculator, subtitle: v } })} multiline />
      {CALC_TEXT_FIELDS.map(([k, label, multiline]) => (
        <Field
          key={k}
          label={label}
          value={data.flexfuel_calculator?.[k] ?? CALC_TEXT_DEFAULTS[k]}
          onChange={(v) => setData({ ...data, flexfuel_calculator: { ...data.flexfuel_calculator, [k]: v } })}
          multiline={multiline}
          testId={`content-calc-${k}`}
        />
      ))}
    </Section>

    <Section title="Section Témoignages Google" defaultOpen={false}>
      <Field label="Badge" value={data.testimonials?.badge} onChange={(v) => setData({ ...data, testimonials: { ...data.testimonials, badge: v } })} />
      <Field label="Titre" value={data.testimonials?.title} onChange={(v) => setData({ ...data, testimonials: { ...data.testimonials, title: v } })} />
      <Field label="Sous-titre (optionnel)" value={data.testimonials?.subtitle} onChange={(v) => setData({ ...data, testimonials: { ...data.testimonials, subtitle: v } })} multiline />
      <Field label="Texte du bouton 'Voir tous les avis'" value={data.testimonials?.cta_leave_review} onChange={(v) => setData({ ...data, testimonials: { ...data.testimonials, cta_leave_review: v } })} />
    </Section>

    <Section title="Section Prise de RDV (Booking)" defaultOpen={false}>
      <Field label="Badge" value={data.booking?.badge} onChange={(v) => setData({ ...data, booking: { ...data.booking, badge: v } })} />
      <Field label="Titre" value={data.booking?.title} onChange={(v) => setData({ ...data, booking: { ...data.booking, title: v } })} />
      <Field label="Sous-titre (optionnel)" value={data.booking?.subtitle} onChange={(v) => setData({ ...data, booking: { ...data.booking, subtitle: v } })} multiline />
      <Field label="Bouton final de confirmation" value={data.booking?.cta_confirm} onChange={(v) => setData({ ...data, booking: { ...data.booking, cta_confirm: v } })} />
    </Section>

    <Section title="Pied de page (coordonnées)">
      <Field label="Adresse (lignes séparées par virgule)" value={data.footer?.address} onChange={(v) => setData({ ...data, footer: { ...data.footer, address: v } })} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Téléphone" value={data.footer?.phone} onChange={(v) => setData({ ...data, footer: { ...data.footer, phone: v } })} />
        <Field label="Email" value={data.footer?.email} onChange={(v) => setData({ ...data, footer: { ...data.footer, email: v } })} />
      </div>
    </Section>
  </>
);

const SortableFSServiceRow = ({ s, idx, onChange, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `fs-svc-${idx}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            data-testid={`fs-svc-drag-${idx}`}
            className="text-white/30 hover:text-white/60 cursor-grab active:cursor-grabbing"
            aria-label="Réordonner"
          >
            <GripVertical size={16} />
          </button>
          <span className="text-white/40 text-xs">Prestation {idx + 1}</span>
        </div>
        <button
          data-testid={`content-service-delete-${idx}`}
          onClick={() => onDelete(idx)}
          className="text-red-400 hover:text-red-300"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Titre" value={s.title} onChange={(v) => onChange(idx, { title: v })} />
        <Field label="Prix" value={s.price} onChange={(v) => onChange(idx, { price: v })} />
      </div>
      <Field label="Description" value={s.desc} onChange={(v) => onChange(idx, { desc: v })} multiline />
    </div>
  );
};

const FSLandingEditor = ({ data, setData }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const updateServiceAt = (idx, patch) => {
    const services = [...(data.services || [])];
    services[idx] = { ...services[idx], ...patch };
    setData({ ...data, services });
  };
  const deleteServiceAt = (idx) => {
    const services = [...(data.services || [])];
    services.splice(idx, 1);
    setData({ ...data, services });
  };
  const handleServicesDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = parseInt(String(active.id).replace("fs-svc-", ""), 10);
    const newIdx = parseInt(String(over.id).replace("fs-svc-", ""), 10);
    setData({ ...data, services: arrayMove(data.services || [], oldIdx, newIdx) });
  };

  return (
  <>
    <PageSEOEditor data={data} setData={setData} pageLabel="Service Fichiers" pageRoute="/file-service" />
    <Section title="Hero">
      <Field label="Badge" value={data.hero?.badge} onChange={(v) => setData({ ...data, hero: { ...data.hero, badge: v } })} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Titre ligne 1" value={data.hero?.title_line_1} onChange={(v) => setData({ ...data, hero: { ...data.hero, title_line_1: v } })} />
        <Field label="Titre ligne 2 (rouge)" value={data.hero?.title_line_2} onChange={(v) => setData({ ...data, hero: { ...data.hero, title_line_2: v } })} />
      </div>
      <Field label="Sous-titre" value={data.hero?.subtitle} onChange={(v) => setData({ ...data, hero: { ...data.hero, subtitle: v } })} multiline />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Texte bouton principal" value={data.hero?.cta_primary} onChange={(v) => setData({ ...data, hero: { ...data.hero, cta_primary: v } })} />
        <Field label="Texte lien secondaire" value={data.hero?.cta_secondary} onChange={(v) => setData({ ...data, hero: { ...data.hero, cta_secondary: v } })} />
      </div>
    </Section>

    <Section title="Les 3 étapes">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Badge" value={data.steps_badge} onChange={(v) => setData({ ...data, steps_badge: v })} />
        <Field label="Titre" value={data.steps_title} onChange={(v) => setData({ ...data, steps_title: v })} />
      </div>
      {(data.steps || []).map((s, idx) => (
        <div key={idx} className="bg-[#1C1C1E] border border-white/[0.04] rounded-md p-3 space-y-2">
          <span className="text-white/40 text-xs">Étape {idx + 1}</span>
          <Field label="Titre" value={s.title} onChange={(v) => {
            const steps = [...data.steps];
            steps[idx] = { ...steps[idx], title: v };
            setData({ ...data, steps });
          }} />
          <Field label="Description" value={s.desc} onChange={(v) => {
            const steps = [...data.steps];
            steps[idx] = { ...steps[idx], desc: v };
            setData({ ...data, steps });
          }} multiline />
        </div>
      ))}
    </Section>

    <Section title="Prestations / Tarifs">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Badge" value={data.services_badge} onChange={(v) => setData({ ...data, services_badge: v })} />
        <Field label="Titre" value={data.services_title} onChange={(v) => setData({ ...data, services_title: v })} />
      </div>
      <Field label="Note (optionnel)" value={data.services_note} onChange={(v) => setData({ ...data, services_note: v })} multiline />
      <p className="text-white/30 text-xs">
        Glisse-dépose via <GripVertical size={12} className="inline -mt-0.5" /> pour réordonner. N&apos;oublie pas « Enregistrer » en haut pour sauvegarder.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleServicesDragEnd}>
        <SortableContext
          items={(data.services || []).map((_, i) => `fs-svc-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {(data.services || []).map((s, idx) => (
              <SortableFSServiceRow
                key={idx}
                s={s}
                idx={idx}
                onChange={updateServiceAt}
                onDelete={deleteServiceAt}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <button
        data-testid="content-service-add"
        onClick={() => {
          const services = [...(data.services || []), { title: "Nouvelle prestation", price: "Sur devis", desc: "" }];
          setData({ ...data, services });
        }}
        className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
      >
        <Plus size={14} /> Ajouter une prestation
      </button>
    </Section>

    <Section title="Nos engagements">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Badge" value={data.advantages_badge} onChange={(v) => setData({ ...data, advantages_badge: v })} />
        <Field label="Titre" value={data.advantages_title} onChange={(v) => setData({ ...data, advantages_title: v })} />
      </div>
      {(data.advantages || []).map((a, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input
            type="text"
            value={a}
            onChange={(e) => {
              const advantages = [...data.advantages];
              advantages[idx] = e.target.value;
              setData({ ...data, advantages });
            }}
            className="flex-1 bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
          />
          <button
            data-testid={`content-advantage-delete-${idx}`}
            onClick={() => {
              const advantages = [...data.advantages];
              advantages.splice(idx, 1);
              setData({ ...data, advantages });
            }}
            className="text-red-400 hover:text-red-300 p-2"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        data-testid="content-advantage-add"
        onClick={() => {
          const advantages = [...(data.advantages || []), "Nouvel engagement"];
          setData({ ...data, advantages });
        }}
        className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-medium flex items-center gap-1.5"
      >
        <Plus size={14} /> Ajouter un engagement
      </button>
    </Section>

    <Section title="CTA final">
      <Field label="Titre" value={data.final_cta?.title} onChange={(v) => setData({ ...data, final_cta: { ...data.final_cta, title: v } })} />
      <Field label="Sous-titre" value={data.final_cta?.subtitle} onChange={(v) => setData({ ...data, final_cta: { ...data.final_cta, subtitle: v } })} multiline />
      <Field label="Texte du bouton" value={data.final_cta?.button} onChange={(v) => setData({ ...data, final_cta: { ...data.final_cta, button: v } })} />
    </Section>
  </>
  );
};

export default function ContentTab({ initialKey = null }) {
  const [currentKey, setCurrentKey] = useState(initialKey || "home_landing");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadedKeyRef = React.useRef(null);
  const requestIdRef = React.useRef(0);

  const loadContent = async (key) => {
    const rid = ++requestIdRef.current;
    setData(null);       // wipe stale editor immediately on key switch
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/content/${key}?raw=1`);
      // Ignore stale responses (user has switched to another key in the meantime)
      if (rid !== requestIdRef.current) return;
      setData(res.data);
      loadedKeyRef.current = key;
    } catch (err) {
      if (rid !== requestIdRef.current) return;
      toast.error("Erreur au chargement du contenu");
    } finally {
      if (rid === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => { loadContent(currentKey); }, [currentKey]);

  const handleSave = async () => {
    // Safety net: refuse to persist when the loaded key does not match the
    // currently-selected key (would corrupt data across CMS keys).
    if (loadedKeyRef.current !== currentKey) {
      toast.error("Contenu pas encore chargé — attends la fin du chargement");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/content/${currentKey}`, data);
      // Invalidate navbar cache so all <Navbar/> instances refetch immediately
      // (kills the flash-of-stale-items across page transitions).
      if (currentKey === "navbar") {
        invalidateNavbarCache();
      }
      toast.success("Contenu enregistré — les changements sont en ligne");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur à l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Restaurer les textes d'origine ? Les modifications seront perdues.")) return;
    try {
      const res = await axios.post(`${API}/api/admin/content/${currentKey}/reset`);
      setData(res.data.content);
      loadedKeyRef.current = currentKey;
      if (currentKey === "navbar") {
        invalidateNavbarCache();
      }
      toast.success("Textes d'origine restaurés");
    } catch (err) {
      toast.error("Erreur au reset");
    }
  };

  return (
    <div data-testid="admin-content-tab" className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight">
            Contenu <span className="text-[#FF3B30]">éditable</span>
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Modifie directement les textes de tes pages publiques. Les changements sont appliqués immédiatement.
          </p>
        </div>
        <div className="flex items-center gap-2 sm:shrink-0">
          <button
            onClick={handleReset}
            data-testid="content-reset-btn"
            className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 bg-[#1C1C1E] hover:bg-[#2a2a2a] text-white/70 text-sm px-4 py-2.5 sm:py-2 rounded-md border border-white/[0.06] transition-colors"
          >
            <RotateCcw size={14} /> Réinitialiser
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !data}
            data-testid="content-save-btn"
            className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 sm:py-2 rounded-md transition-colors"
          >
            <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* Key selector — organized by category. On mobile the current category
          expands and the others collapse to keep the picker compact. */}
      <div className="space-y-3">
        {CATEGORIES.map((cat) => {
          const isCurrentCat = cat.keys.some((k) => k.key === currentKey);
          return (
            <CategoryPicker
              key={cat.id}
              cat={cat}
              currentKey={currentKey}
              setCurrentKey={setCurrentKey}
              defaultOpenMobile={isCurrentCat}
            />
          );
        })}
      </div>

      {/* Editor */}
      {loading || !data ? (
        <div className="text-white/40 text-sm p-8 text-center">Chargement…</div>
      ) : (
        <div className="space-y-3">
          {currentKey === "home_landing" && <HomeLandingEditor data={data} setData={setData} />}
          {currentKey === "flexfuel_page" && <FlexfuelPageEditor data={data} setData={setData} />}
          {currentKey === "shop_landing" && <ShopLandingEditor data={data} setData={setData} />}
          {currentKey === "fs_landing" && <FSLandingEditor data={data} setData={setData} />}
          {currentKey === "banner" && <BannerEditor data={data} setData={setData} />}
          {currentKey === "contact_info" && <ContactInfoEditor data={data} setData={setData} />}
          {currentKey === "email_templates" && <EmailTemplatesEditor data={data} setData={setData} />}
          {currentKey === "reprog_page" && <ReprogPageEditor data={data} setData={setData} />}
          {currentKey === "achat_vente_page" && <AchatVentePageEditor data={data} setData={setData} />}
          {currentKey === "rep_entretien_page" && <RepEntretienPageEditor data={data} setData={setData} />}
          {currentKey === "navbar" && <NavbarEditor data={data} setData={setData} />}
          {currentKey === "discharge" && <DischargeEditor data={data} setData={setData} />}
          {currentKey === "site_meta" && <SiteMetaEditor data={data} setData={setData} />}
          {currentKey === "review_nudge" && <ReviewNudgeEditor data={data} setData={setData} />}
        </div>
      )}
    </div>
  );
}
